import { useCallback, useEffect, useState } from "react";
import {
  AlfredFiatAccount,
  AlfredFiatAccountFields,
  AlfredFiatAccountType,
  AlfredInitiateWithdrawalTransaction,
  alfredService,
  PaymentType,
  v4Service,
} from "src/api";
import {
  useAlfredQuotePreview,
  useAlfredSupportedOptions,
  useCalculatedLimits,
  useEstimatePaymentFee,
  usePaymentValidation,
} from "src/hooks";
import { useDepositWithdrawContext, useUrlParamsContext } from "src/contexts";
import { onlyNumbers, formatNumberToExchange, _t } from "src/utils";

/**
 * Alfred-only withdrawal orchestrator. Mirrors `useDeposit` in shape:
 *
 *  1. Pick a target fiat (`selectedOption`) — what the user receives.
 *  2. Type a USDC amount (`amount`) — what they spend.
 *  3. Pick one of the saved Alfred payment methods.
 *  4. Confirm — backend mints the offramp at Alfred and (on dev
 *     exchanges) auto-fires the success-path webhook chain so the
 *     stepper marches to FIAT_TRANSFER_COMPLETED. The widget polls
 *     `GET /psp/alfred/session` for live status.
 *
 * Quote direction is the inverse of deposit: source = USDC (stable),
 * target = fiat. Platform fee is computed on the USDC side (route's
 * stable currency, same as deposit).
 */
export const useAlfredWithdrawal = () => {
  const { balance } = useDepositWithdrawContext();
  const { paymentRouteId } = useUrlParamsContext();
  const { currency } = balance;

  const [amount, setAmount] = useState<string>("");
  const [amountTouched, setAmountTouched] = useState<boolean>(false);
  const [selectedFiatAccountId, setSelectedFiatAccountId] = useState<string | null>(null);
  const [fiatAccounts, setFiatAccounts] = useState<AlfredFiatAccount[]>([]);
  const [fiatAccountsLoading, setFiatAccountsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transaction, setTransaction] = useState<AlfredInitiateWithdrawalTransaction | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Add-bank-account inline form state. We keep it on the same hook so
  // the form lives alongside the dropdown — Alfred stores the account,
  // we just refresh the list and auto-select the new entry.
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [addBankLoading, setAddBankLoading] = useState(false);
  const [addBankError, setAddBankError] = useState<string | null>(null);

  const {
    options,
    selected: selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    loading: optionsLoading,
  } = useAlfredSupportedOptions({ type: "withdrawal" });

  // Quote direction: USDC → fiat. `sourceCurrency` here is the wallet
  // currency (stable, route's currency_id) and `targetCurrency` is the
  // user-picked fiat. `useAlfredQuotePreview` is direction-agnostic.
  // Skip the quote fetch when the typed amount exceeds the available
  // balance — there's no point quoting an unsendable withdrawal, and
  // the spam to /quotes is wasted Alfred bandwidth (plus confusing
  // "Receive total" values for a transaction the user can't submit).
  const stableSpend = Number(amount) || 0;
  const hasBalance = stableSpend > 0 && stableSpend <= balance.free_balance;
  const { quote, loading: quoteLoading } = useAlfredQuotePreview({
    sourceCurrency: currency.currency_id || null,
    targetCurrency: selectedOption?.source_currency ?? null,
    amount: hasBalance ? amount : "",
  });
  const fiatReceiveGross = quote ? Number(quote.toAmount) : 0;

  // Platform fee runs against the USDC side — same way deposit does.
  // `paymentType: Withdrawal` flips the right column on the fee_group.
  const { paymentFee, paymentFeeLoading, paymentFeeError } = useEstimatePaymentFee({
    paymentType: PaymentType.Withdrawal,
    amount: stableSpend,
  });

  const {
    limitsStatus,
    availableLimit,
    loading: limitsLoading,
    refetch: refetchLimits,
  } = useCalculatedLimits(PaymentType.Withdrawal);

  const { paymentValidationErrors } = usePaymentValidation({
    amount: stableSpend,
    paymentType: PaymentType.Withdrawal,
    fee: paymentFee,
    availableLimit,
  });

  const handleAmountChange = (next: string) => {
    if (onlyNumbers(next)) {
      // The USDT amount must accept cents. Some accounts report a stable
      // currency precision of 0, which would floor "6.98" → "6"; clamp to
      // at least 2 decimals so the off-ramp input keeps the fractional part.
      const decimals = Math.max(currency.precision || 0, 2);
      setAmount(formatNumberToExchange(next, decimals));
    }
  };

  // Clamp the typed amount into the option's [min, max] when the field
  // loses focus, so the input can't keep an out-of-range value.
  const handleAmountBlur = () => {
    setAmountTouched(true);
    const n = Number(amount);
    if (!amount || !Number.isFinite(n) || !selectedOption) return;
    const min = Number(selectedOption.min_amount);
    const max = Number(selectedOption.max_amount);
    let clamped = n;
    if (Number.isFinite(max) && clamped > max) clamped = max;
    if (Number.isFinite(min) && clamped < min) clamped = min;
    if (clamped !== n) {
      const decimals = Math.max(currency.precision || 0, 2);
      setAmount(formatNumberToExchange(String(clamped), decimals));
    }
  };

  // Lazy-loads beneficiary fiatAccounts once the user picks a fiat. We
  // need a session_id first because the listFiatAccounts endpoint binds
  // the customer via the cached session — so we create it here too.
  const ensureSessionAndLoadAccounts = useCallback(async () => {
    if (!selectedCurrency || sessionId) return;
    setFiatAccountsLoading(true);
    try {
      const session = await v4Service.createSession({
        data: {
          amount: Number(amount) || 1,
          payment_route_id: paymentRouteId,
          source_currency_id: selectedCurrency,
          type: PaymentType.Withdrawal,
        },
      });
      if (!session?.session_id) {
        throw new Error(_t("ERRORS.FAILED_TO_CREATE_SESSION"));
      }
      setSessionId(session.session_id);
      const accounts = await alfredService.listFiatAccounts(session.session_id);
      setFiatAccounts(accounts);
      // Default to the first account; status semantics on Alfred's
      // fiatAccount controller are not as strict as the legacy
      // payment-method `active` filter, so we don't pre-filter here.
      if (accounts[0]) setSelectedFiatAccountId(accounts[0].fiatAccountId);
    } catch (e) {
      console.error("Failed to load Alfred fiat accounts:", e);
    } finally {
      setFiatAccountsLoading(false);
    }
  }, [selectedCurrency, sessionId, paymentRouteId, amount]);

  useEffect(() => {
    void ensureSessionAndLoadAccounts();
  }, [ensureSessionAndLoadAccounts]);

  // NOTE: the amount is bound to the session at submit time
  // (`handleInitiateWithdrawal`), not in a debounced effect. The old
  // per-keystroke `createSession` raced on rapid edits — responses
  // resolved out of order, so the last one to land won `sessionId`
  // regardless of which amount it carried, and the offramp ran against a
  // stale amount (user typed 0.5, but a slower 1000-session response
  // pinned 1000). Creating the session synchronously at submit removes
  // the race entirely.

  // Snapshot of the submit so the success screen can render
  // amount/quote without re-deriving from form state (which the user
  // can keep editing after the call returns). Captured at submit
  // time, cleared on reset.
  const [submittedSnapshot, setSubmittedSnapshot] = useState<{
    fromAmount: string;
    fromCurrency: string;
    toAmount: string;
    toCurrency: string;
  } | null>(null);

  const handleInitiateWithdrawal = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    const numeric = Number(amount);
    if (!selectedFiatAccountId || !selectedCurrency || !Number.isFinite(numeric) || numeric <= 0) {
      setSubmitError(_t("ERRORS.MISSING_REQUIRED_DATA_FOR_WITHDRAWAL"));
      return;
    }
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      // Mint a fresh session bound to the exact amount being confirmed,
      // so the offramp can never run against a stale/racy session amount.
      // Beneficiary fiatAccounts are customer-scoped, so the id selected
      // under the load-time session stays valid on this new session.
      const session = await v4Service.createSession({
        data: {
          amount: numeric,
          payment_route_id: paymentRouteId,
          source_currency_id: selectedCurrency,
          type: PaymentType.Withdrawal,
        },
      });
      if (!session?.session_id) {
        throw new Error(_t("ERRORS.FAILED_TO_CREATE_SESSION"));
      }
      setSessionId(session.session_id);
      const res = await alfredService.initiateWithdrawal({
        session_id: session.session_id,
        fiat_account_id: selectedFiatAccountId,
        // Sent so the backend can reject if it disagrees with the
        // session amount — defence-in-depth against stale sessions.
        amount: numeric,
      });
      // Capture the summary the moment the call succeeds — independent
      // of whether `res.transaction` is populated (manual-review path
      // returns null transaction; Alfred /offramp fires after admin
      // approval). The widget should always switch to the "submitted"
      // confirmation screen on a 2xx so the user gets feedback.
      setSubmittedSnapshot({
        fromAmount: amount,
        fromCurrency: currency.currency_id || selectedOption?.target_currency || "",
        toAmount: quote ? quote.toAmount : "",
        toCurrency: selectedOption?.source_currency ?? "",
      });
      if (res?.transaction) setTransaction(res.transaction);
    } catch (err) {
      console.error("Alfred initiateWithdrawal failed:", err);
      setSubmitError(_t("ERRORS.FAILED_TO_PROCESS_WITHDRAWAL"));
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetWithdrawal = () => {
    setTransaction(null);
    setSubmittedSnapshot(null);
    setAmount("");
    setSubmitError(null);
    // The withdrawal just consumed limit headroom — pull fresh used totals
    // for the form we're returning to.
    refetchLimits();
  };

  const refreshFiatAccounts = useCallback(
    async (sid: string, prefer?: string) => {
      const accounts = await alfredService.listFiatAccounts(sid);
      setFiatAccounts(accounts);
      const next =
        (prefer && accounts.find((a) => a.fiatAccountId === prefer)) ??
        accounts[0] ??
        null;
      if (next) setSelectedFiatAccountId(next.fiatAccountId);
    },
    [],
  );

  const handleAddFiatAccount = useCallback(
    async (payload: { type: AlfredFiatAccountType; fiatAccountFields: AlfredFiatAccountFields; isExternal?: boolean }) => {
      if (!sessionId) {
        setAddBankError(_t("ERRORS.MISSING_REQUIRED_DATA_FOR_WITHDRAWAL"));
        return;
      }
      setAddBankLoading(true);
      setAddBankError(null);
      try {
        const created = await alfredService.createFiatAccount({
          session_id: sessionId,
          ...payload,
        });
        await refreshFiatAccounts(sessionId, created.fiatAccountId);
        setAddBankOpen(false);
      } catch (err: any) {
        // Surface Alfred's `errorMessage` (and the offending field set
        // when the validator returns errorCode 110002) so the user
        // sees exactly which field needs fixing instead of a generic
        // "request failed".
        const apiMsg =
          err?.response?.data?.errorMessage ??
          err?.message ??
          _t("ERRORS.GENERIC");
        const fields = err?.response?.data?.errorMetadata?.fields;
        setAddBankError(fields?.length ? `${apiMsg}: ${fields.join(", ")}` : String(apiMsg));
      } finally {
        setAddBankLoading(false);
      }
    },
    [sessionId, refreshFiatAccounts],
  );

  // Alfred per-rail amount limits. For offramp the option's
  // min_amount/max_amount are on the stable (USDT) side — the same side
  // the user types — so we compare against `stableSpend` and label the
  // message in the wallet currency. Mirrors the deposit form, which
  // already enforces these (useDepositValidation).
  const limitErrors: string[] = [];
  if (selectedOption && stableSpend > 0) {
    const min = Number(selectedOption.min_amount);
    const max = Number(selectedOption.max_amount);
    if (Number.isFinite(min) && stableSpend < min) {
      limitErrors.push(
        _t("VALIDATION.MINIMUM_AMOUNT", {
          paymentType: PaymentType.Withdrawal,
          minAmount: selectedOption.min_amount,
          currency: currency.currency_id,
        })
      );
    }
    if (Number.isFinite(max) && stableSpend > max) {
      limitErrors.push(
        _t("VALIDATION.MAXIMUM_AMOUNT", {
          paymentType: PaymentType.Withdrawal,
          maxAmount: selectedOption.max_amount,
          currency: currency.currency_id,
        })
      );
    }
  }

  const errors = {
    inputErrors: [
      ...(!amount ? [_t("VALIDATION.AMOUNT_SHOULD_NOT_BE_EMPTY")] : []),
      ...(stableSpend <= 0 ? [_t("VALIDATION.AMOUNT_SHOULD_BE_GREATER_THAN_ZERO")] : []),
    ],
    limitErrors,
    paymentValidationErrors,
    paymentMethodErrors: !selectedFiatAccountId ? [_t("VALIDATION.PAYMENT_METHOD_REQUIRED")] : [],
    paymentFeeErrors: paymentFeeError ? [_t("VALIDATION.ERROR_ESTIMATING_PAYMENT_FEE")] : [],
    balanceErrors:
      stableSpend > 0 && stableSpend > balance.free_balance
        ? [_t("VALIDATION.NOT_ENOUGH_BALANCE", { balance: balance.free_balance })]
        : [],
  };

  const submitDisabled =
    paymentFeeLoading ||
    quoteLoading ||
    submitLoading ||
    !sessionId ||
    !selectedFiatAccountId ||
    Object.values(errors).some((e) => e.length > 0);

  return {
    amount,
    amountTouched,
    setAmountTouched,
    handleAmountChange,
    handleAmountBlur,
    options,
    selectedOption,
    selectedCurrency,
    setSelectedCurrency,
    optionsLoading,
    fiatAccounts,
    fiatAccountsLoading,
    selectedFiatAccountId,
    setSelectedFiatAccountId,
    addBankOpen,
    setAddBankOpen,
    addBankLoading,
    addBankError,
    handleAddFiatAccount,
    quote,
    quoteLoading,
    limitsStatus,
    availableLimit,
    limitsLoading,
    fiatReceiveGross,
    netStableSpend: stableSpend + paymentFee,
    paymentFee,
    paymentFeeLoading,
    balance,
    currency,
    errors,
    submitDisabled,
    submitLoading,
    submitError,
    handleInitiateWithdrawal,
    sessionId,
    transaction,
    submittedSnapshot,
    resetWithdrawal,
  };
};
